import { isAxiosError } from 'axios'
import { flatten, join, pathOr, pipe, values } from 'ramda'

export const GENERIC_ERROR_MESSAGE = 'Coś poszło nie tak'

const getResponseData = (error: unknown): unknown => {
  if (isAxiosError(error)) {
    return error.response?.data
  }

  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    return error.response.data
  }

  return undefined
}

export const extractMessageFromErrorResponse = (
  error: unknown,
  fallbackMessage: string = GENERIC_ERROR_MESSAGE
): string => {
  const data = getResponseData(error)

  if (
    data &&
    typeof data === 'object' &&
    'message' in data &&
    typeof data.message === 'string'
  ) {
    return data.message
  }

  return fallbackMessage
}

/**
 * The API returns a stable `code` alongside `message` on some errors, so
 * callers can branch on the case instead of matching a translated string.
 */
export const extractCodeFromErrorResponse = (
  error: unknown
): string | undefined => {
  const data = getResponseData(error)

  if (
    data &&
    typeof data === 'object' &&
    'code' in data &&
    typeof data.code === 'string'
  ) {
    return data.code
  }

  return undefined
}

export const getApiErrors = error => {
  const getErrorsFromResponse = pipe(
    pathOr({}, ['response', 'data', 'errors']),
    values,
    flatten,
    join('\n\n')
  )(error)

  // if there is only one error, it must be handled with previous helper
  return getErrorsFromResponse || extractMessageFromErrorResponse(error)
}

export const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }

  return String(error)
}

export interface ApiErrorResponse {
  data: unknown
  status: number
  statusText: string
}

/**
 * Error thrown by the server-side fetch API client on non-OK responses.
 * Keeps the axios-like `response` shape so existing `error.response.*`
 * consumers and status helpers keep working, while being a real Error
 * (Sentry gets a message + stack instead of "Object captured as exception").
 */
export class ApiError extends Error {
  readonly response: ApiErrorResponse
  readonly endpoint: string

  constructor(endpoint: string, response: ApiErrorResponse) {
    // Query strings can carry sensitive params (tokens) and explode Sentry
    // title cardinality — never persist them on the error. Path only.
    const endpointPath = endpoint.split('?')[0]

    super(
      `API request failed with ${response.status} ${response.statusText} (${endpointPath})`
    )
    this.name = 'ApiError'
    this.endpoint = endpointPath
    this.response = response
  }
}

export const getResponseStatus = (error: unknown): number | undefined => {
  if (isAxiosError(error)) {
    return error.response?.status
  }

  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    typeof error.response.status === 'number'
  ) {
    return error.response.status
  }

  return undefined
}

export const is404Error = (error: unknown): boolean =>
  getResponseStatus(error) === 404

/** 429 from the API — expected under bot/scanner traffic, not an app defect. */
export const isRateLimitError = (error: unknown): boolean =>
  getResponseStatus(error) === 429

/**
 * Safari/WebKit throws DOMException SecurityError on storage/cookie access
 * in private mode or with blocked cookies — expected degradation, not a bug.
 */
export const isSecurityError = (error: unknown): boolean =>
  typeof DOMException !== 'undefined' &&
  error instanceof DOMException &&
  error.name === 'SecurityError'

/** Normalize an unknown thrown value into a real Error (for Sentry & logging). */
export const ensureError = (
  value: unknown,
  fallbackMessage?: string
): Error => {
  if (value instanceof Error) return value

  const status = getResponseStatus(value)
  const message = status
    ? `API response error (${status})`
    : fallbackMessage || `Non-Error value thrown: ${getErrorMessage(value)}`
  const error = new Error(message)

  error.cause = value

  return error
}
